<template>
  <div 
    class="image-comparison" 
    ref="container"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <!-- 底層圖片 -->
    <div class="image-wrapper bottom-image">
      <!-- 添加響應式圖片 -->
      <picture style="width: 100%;">
        <!-- 平板版 (768px 以上) -->
        <source media="(min-width: 768px)" srcset="/images/desktop/scale_new.jpg">
        <!-- 手機版 (767px 以下) -->
        <img src="/images/mobile/scale_new.png" class="image-item" alt="高解析度放大後的圖片">
      </picture>
    </div>
    
    <!-- 頂層圖片 (帶遮罩) -->
    <div 
      class="image-wrapper top-image"
      :style="{ clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)` }"
    >
      <!-- 添加響應式圖片 -->
      <picture style="width: 100%;">
        <!-- 平板版 (768px 以上) -->
        <source media="(min-width: 768px)" srcset="/images/desktop/scale_old.jpg">
        <!-- 手機版 (767px 以下) -->
        <img src="/images/mobile/scale_old.png" class="image-item" alt="原始低解析度圖片">
      </picture>
    </div>
    
    <!-- 分割線 -->
    <div 
      class="divider" 
      :style="{ left: position + '%' }"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const container = ref(null)
const position = ref(50) // 分割線位置 (0-100%)
const isDragging = ref(false)

// 更新滑塊位置
const updatePosition = (clientX) => {
  if (!isDragging.value || !container.value) return
  
  const rect = container.value.getBoundingClientRect()
  const x = clientX - rect.left
  const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100))
  position.value = percentage
}

// 開始拖拽
const startDrag = (e) => {
  isDragging.value = true
  const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX
  updatePosition(clientX)
  e.preventDefault()
}

// 拖拽中
const onDrag = (e) => {
  if (!isDragging.value) return
  const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX
  updatePosition(clientX)
  e.preventDefault()
}

// 結束拖拽
const endDrag = () => {
  isDragging.value = false
}

// 綁定事件
onMounted(() => {
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', endDrag)
  document.addEventListener('touchmove', onDrag, { passive: false })
  document.addEventListener('touchend', endDrag)
})

// 清理事件
onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', endDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', endDrag)
})
</script>

<style scoped>
.image-comparison {
  background: transparent; /* 移除背景色 */
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 1; /* 固定長寬比 */
  min-height: 100px; /* 設定最小高度 */
  margin: 0 auto;
  overflow: hidden;
  border-radius: 10px;
  cursor: col-resize;
}

.image-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-wrapper .image-item {
  width: 100%;
  object-fit: cover; /* 關鍵設定：確保圖片填滿容器 */
  object-position: center; /* 圖片居中顯示 */
}

.bottom-image {
  z-index: 1;
}

.top-image {
  z-index: 2;
}

.divider {
  position: absolute;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #fff, #ddd, #fff);
  transform: translateX(-50%);
  z-index: 10;
  box-shadow: 0 0 10px rgba(0,0,0,0.5);
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 30px;
  height: 30px;
  background: #fff;
  border: 2px solid #ddd;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
}

.divider::after {
  content: '⟷';
  position: absolute;
  top: 48.8%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #666;
  font-size: 24px;
  font-weight: bold;
}
@media (max-width: 768px) {
  .image-comparison {
    aspect-ratio: 2 / 1; /* 固定長寬比 */
    min-height: 100px; /* 設定最小高度 */
  }
}
</style>